import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { FaUser, FaSchool, FaMapMarkerAlt, FaGraduationCap, FaPhone, FaCode, FaPaperPlane, FaCheckCircle, FaEnvelope, FaExclamationTriangle } from "react-icons/fa";
import TopNavbar from "../components/Nav/TopNavbar";
import Footer from "../components/Sections/Footer";
import AnimateOnScroll from "../components/AnimateOnScroll";
import { submitStudentApplication, getCenters, getRegions } from "../services/studentApi";

export default function Application() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    formerSchool: '',
    option: '',
    school: '',
    field: '',
    centerOfChoice: '',
    region: '',
    contact: '',
    referralCode: ''
  });
  const [centers, setCenters] = useState([]);
  const [centersLoading, setCentersLoading] = useState(true);
  const [centersError, setCentersError] = useState(null);
  const [regions, setRegions] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [regionsError, setRegionsError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    getCenters()
      .then(setCenters)
      .catch((err) => setCentersError(err.message || 'Failed to load centers'))
      .finally(() => setCentersLoading(false));
  }, []);

  useEffect(() => {
    getRegions()
      .then(setRegions)
      .catch((err) => setRegionsError(err.message || 'Failed to load regions'))
      .finally(() => setRegionsLoading(false));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Clear school and field when option changes
      ...(name === 'option' ? { school: '', field: '' } : {}),
      // Clear field when school changes
      ...(name === 'school' ? { field: '' } : {})
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Get school options based on selected option
  const getSchoolOptions = () => {
    if (formData.option === 'Medical') {
      return [
        { value: 'fhs-university-bamenda', label: 'FHS University of Bamenda' },
        { value: 'fhs-university-buea', label: 'FHS University of Buea' },
        { value: 'fmbs-catuc-bamenda', label: 'FMBS CATUC Bamenda' },
        { value: 'fmbs-university-yaounde1', label: 'FMBS University of Yaoundé 1' },
        { value: 'fmbs-university-douala', label: 'FMBS University of Douala' },
        { value: 'fmbs-university-garoua', label: 'FMBS University of Garoua' },
        { value: 'fmbs-university-dschang', label: 'FMBS University of Dschang' },
        { value: 'fmbs-university-bertoua', label: 'FMBS University of Bertoua' },
        { value: 'istm-bangante', label: 'ISTM Bangante' }
      ];
    } else if (formData.option === 'Engineering') {
      return [
        { value: 'nahpi', label: 'NAHPI (National Advanced School of Public Works)' },
        { value: 'coltech', label: 'COLTECH (College of Technology)' },
        { value: 'cot', label: 'COT (College of Technology)' },
        { value: 'fet', label: 'FET (Faculty of Engineering and Technology)' },
        { value: 'public-works', label: 'Public Works' },
        { value: 'polytechnic-yaounde', label: 'Polytechnic Yaoundé' },
        { value: 'polytechnic-douala', label: 'Polytechnic Douala' },
       
      ];
    } else if (formData.option === 'Other') {
      return [
        { value: 'hicm', label: 'HICM (Logistics, Accounting and Marketing)' },
        { value: 'hitl', label: 'HITL (Logistics, Accounting and Marketing)' },
        { value: 'httc', label: 'HTTC (All Arts and Science Subjects)' },
        { value: 'httc-trade', label: 'HTTTC (All Subjects and Trade)' },
        { value: 'customs', label: 'CUSTOMS' },
        { value: 'enam', label: 'ENAM' },
        { value: 'other-professional', label: 'Other Professional Schools' }
      ];
    }
    return [];
  };

  // Get field options based on selected school
  const getFieldOptions = () => {
    // Medical fields - same for all medical schools
    if (formData.option === 'Medical' || 
        formData.school === 'fhs-university-bamenda' ||
        formData.school === 'fhs-university-buea' ||
        formData.school === 'fmbs-catuc-bamenda' ||
        formData.school === 'fmbs-university-yaounde1' ||
        formData.school === 'fmbs-university-douala' ||
        formData.school === 'fmbs-university-garoua' ||
        formData.school === 'fmbs-university-dschang' ||
        formData.school === 'fmbs-university-bertoua' ||
        formData.school === 'istm-bangante') {
      return [
        { value: 'general-medicine', label: 'General Medicine' },
        { value: 'nursing', label: 'Nursing' },
        { value: 'midwifery', label: 'Midwifery' },
        { value: 'pharmacy', label: 'Pharmacy' },
        { value: 'dentistry', label: 'Dentistry' },
        { value: 'pharmacy-technician', label: 'Pharmacy Technician' },
        { value: 'physiotherapist', label: 'Physiotherapist' },
        { value: 'medical-laboratory-scientist', label: 'Medical Laboratory Scientist' },
        { value: 'biomedical-scientist', label: 'Biomedical Scientist' }
      ];
    }
    // Engineering fields - same for all engineering schools including polytechnic
    else if (formData.option === 'Engineering' || 
             formData.school === 'nahpi' || 
             formData.school === 'coltech' || 
             formData.school === 'cot' || 
             formData.school === 'fet' || 
             formData.school === 'public-works' ||
             formData.school === 'polytechnic-yaounde' || 
             formData.school === 'polytechnic-douala' ||
             formData.school === 'civil-engineering' ||
             formData.school === 'mechanical-engineering' ||
             formData.school === 'electrical-engineering' ||
             formData.school === 'computer-engineering' ||
             formData.school === 'architecture') {
      return [
        { value: 'computer-engineering', label: 'Computer Engineering' },
        { value: 'electrical-engineering', label: 'Electrical Engineering' },
        { value: 'mechanical-engineering', label: 'Mechanical Engineering' },
        { value: 'civil-engineering', label: 'Civil Engineering' },
        { value: 'petroleum-engineering', label: 'Petroleum Engineering' },
        { value: 'meteorology-engineering', label: 'Meteorology Engineering' },
        { value: 'mining-mineral-engineering', label: 'Mining and Mineral Engineering' },
        { value: 'agricultural-engineering', label: 'Agricultural Engineering' },
        { value: 'software-engineering', label: 'Software Engineering' },
        { value: 'biomedical-engineering', label: 'Bio Medical Engineering' },
        { value: 'biotechnology-food-processing', label: 'Bio-Technology and Food Processing Engineering' },
        { value: 'biochemical-engineering', label: 'Bio-Chemical Engineering' },
        { value: 'chemical-engineering', label: 'Chemical Engineering' }
      ];
    }
    // Other fields
    else if (formData.school === 'hicm' || formData.school === 'hitl') {
      return [
        { value: 'logistics', label: 'Logistics' },
        { value: 'accounting', label: 'Accounting' },
        { value: 'marketing', label: 'Marketing' }
      ];
    } else if (formData.school === 'httc') {
      return [
        { value: 'arts', label: 'Arts Subjects' },
        { value: 'science', label: 'Science Subjects' },
        { value: 'all-arts-science', label: 'All Arts and Science Subjects' }
      ];
    } else if (formData.school === 'httc-trade') {
      return [
        { value: 'all-subjects', label: 'All Subjects' },
        { value: 'trade', label: 'Trade' },
        { value: 'all-subjects-trade', label: 'All Subjects and Trade' }
      ];
    } else if (formData.school === 'customs') {
      return [
        { value: 'customs', label: 'Customs' }
      ];
    } else if (formData.school === 'enam') {
      return [
        { value: 'enam', label: 'ENAM' }
      ];
    } else if (formData.school === 'other-professional') {
      return [
        { value: 'other', label: 'Other' }
      ];
    }
    return [];
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.formerSchool.trim()) newErrors.formerSchool = 'Former school is required';
    if (!formData.option) newErrors.option = 'Option is required';
    if (formData.option && !formData.school) {
      newErrors.school = 'School is required';
    }
    if (formData.school && !formData.field) {
      newErrors.field = 'Field is required';
    }
    if (!formData.centerOfChoice) newErrors.centerOfChoice = 'Center of choice is required';
    if (!formData.region) newErrors.region = 'Region is required';
    if (!formData.contact.trim()) {
      newErrors.contact = 'Contact is required';
    } else if (!/^\+?[\d\s-()]+$/.test(formData.contact)) {
      newErrors.contact = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    setErrors({});
    
    try {
      // Map form data to API format
      const apiData = {
        name: formData.name,
        email: formData.email,
        former_school: formData.formerSchool,
        center: formData.centerOfChoice,
        region: formData.region,
        concour: formData.school ? [formData.school] : [], // Array of schools
        field: formData.field,
        contact: formData.contact,
        referral: formData.referralCode || undefined // Optional field
      };

      const result = await submitStudentApplication(apiData);
      
      if (result.success) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          formerSchool: '',
          option: '',
          school: '',
          field: '',
          centerOfChoice: '',
          region: '',
          contact: '',
          referralCode: ''
        });
        
        // Reset success message after 8 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 8000);
      } else {
        // Handle API errors
        setSubmitError(result.message || 'Failed to submit application. Please try again.');
        
        // Set field-specific errors if provided
        if (result.fieldErrors && Object.keys(result.fieldErrors).length > 0) {
          setErrors(result.fieldErrors);
        }
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      setSubmitError('An unexpected error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://afriqcm.com';
  const currentUrl = typeof window !== 'undefined' ? window.location.href : `${siteUrl}/apply`;

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>Student Application | Apply to AFRIQ Bamenda - Concours Preparation</title>
        <meta name="description" content="Apply to AFRIQ Bamenda for structured concours preparation. Join our programs for medical, engineering, and professional school entrance exam preparation in Cameroon." />
        <meta name="keywords" content="AFRIQ application, student application, concours preparation application, medical school preparation, engineering school preparation, Cameroon, Bamenda" />
        <link rel="canonical" href={currentUrl} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content="Student Application | Apply to AFRIQ Bamenda" />
        <meta property="og:description" content="Apply to AFRIQ Bamenda for structured concours preparation and academic excellence." />
        <meta property="og:image" content={`${siteUrl}/logo512.png`} />
        
        {/* Structured Data - WebPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Student Application - AFRIQ Bamenda",
            "description": "Apply to AFRIQ Bamenda for concours preparation programs",
            "url": currentUrl
          })}
        </script>
      </Helmet>
      <TopNavbar />
      <Wrapper>
        <div className="container">
          <AnimateOnScroll animation="fadeUp" delay={0} duration={800}>
            <HeaderSection>
              <h1 className="font40 extraBold">AFRIQ Bamenda Student Application</h1>
              <p className="font16">
                Join AFRIQ Bamenda and start your journey towards academic excellence. 
                Fill out the application form below to begin your educational transformation.
              </p>
            </HeaderSection>
          </AnimateOnScroll>

          <ApplicationForm>
            <AnimateOnScroll animation="scaleUp" delay={200} duration={800}>
              <FormCard>
                <FormHeader>
                  <h2 className="font25 extraBold">Application Form</h2>
                  <p className="font14">Please fill in all required fields accurately</p>
                </FormHeader>

              {isSubmitted ? (
                <SuccessMessage>
                  <FaCheckCircle />
                  <h3 className="font20 extraBold">Application Submitted Successfully!</h3>
                  <p className="font14">Thank you for applying to AFRIQ Bamenda. A confirmation email with your unique matricule will be sent to your email address. We'll contact you soon with next steps.</p>
                </SuccessMessage>
              ) : (
                <Form onSubmit={handleSubmit}>
                  {submitError && (
                    <ErrorMessageBox>
                      <FaExclamationTriangle />
                      <span>{submitError}</span>
                    </ErrorMessageBox>
                  )}
                  
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="name" className="font14 semiBold">
                        <FaUser />
                        Name *
                      </Label>
                      <Input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={errors.name ? 'error' : ''}
                        placeholder="Input Your name"
                      />
                      {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="email" className="font14 semiBold">
                        <FaEnvelope />
                        Email Address *
                      </Label>
                      <Input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={errors.email ? 'error' : ''}
                        placeholder="Enter your email address"
                      />
                      {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <Label htmlFor="formerSchool" className="font14 semiBold">
                      <FaSchool />
                      Former Secondary School *
                    </Label>
                    <Input
                      type="text"
                      id="formerSchool"
                      name="formerSchool"
                      value={formData.formerSchool}
                      onChange={handleInputChange}
                      className={errors.formerSchool ? 'error' : ''}
                      placeholder="Input Your Former School"
                    />
                    {errors.formerSchool && <ErrorMessage>{errors.formerSchool}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="option" className="font14 semiBold">
                      <FaGraduationCap />
                      Option *
                    </Label>
                    <Select
                      id="option"
                      name="option"
                      value={formData.option}
                      onChange={handleInputChange}
                      className={errors.option ? 'error' : ''}
                    >
                      <option value="">Select your option</option>
                      <option value="Medical">Medical</option>
                      <option value="Engineering">Engineering</option>
                      <option value="Other">Other</option>
                    </Select>
                    {errors.option && <ErrorMessage>{errors.option}</ErrorMessage>}
                  </FormGroup>

                  {formData.option && (
                    <FormGroup>
                      <Label htmlFor="school" className="font14 semiBold">
                        <FaGraduationCap />
                        School *
                      </Label>
                      <Select
                        id="school"
                        name="school"
                        value={formData.school}
                        onChange={handleInputChange}
                        className={errors.school ? 'error' : ''}
                      >
                        <option value="">Select school</option>
                        {getSchoolOptions().map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                      {errors.school && <ErrorMessage>{errors.school}</ErrorMessage>}
                    </FormGroup>
                  )}

                  {formData.school && (
                    <FormGroup>
                      <Label htmlFor="field" className="font14 semiBold">
                        <FaGraduationCap />
                        Field *
                      </Label>
                      <Select
                        id="field"
                        name="field"
                        value={formData.field}
                        onChange={handleInputChange}
                        className={errors.field ? 'error' : ''}
                      >
                        <option value="">Select field</option>
                        {getFieldOptions().map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                      {errors.field && <ErrorMessage>{errors.field}</ErrorMessage>}
                    </FormGroup>
                  )}

                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="centerOfChoice" className="font14 semiBold">
                        <FaMapMarkerAlt />
                        Center of Choice *
                      </Label>
                      <Select
                        id="centerOfChoice"
                        name="centerOfChoice"
                        value={formData.centerOfChoice}
                        onChange={handleInputChange}
                        className={errors.centerOfChoice ? 'error' : ''}
                        disabled={centersLoading}
                      >
                        <option value="">{centersLoading ? 'Loading...' : 'Select'}</option>
                        {centers.map((c) => (
                          <option key={c.id} value={c.name}>
                            {c.full_name || c.name}
                          </option>
                        ))}
                      </Select>
                      {centersError && <ErrorMessage>{centersError}</ErrorMessage>}
                      {errors.centerOfChoice && <ErrorMessage>{errors.centerOfChoice}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="region" className="font14 semiBold">
                        <FaMapMarkerAlt />
                        Region *
                      </Label>
                      <Select
                        id="region"
                        name="region"
                        value={formData.region}
                        onChange={handleInputChange}
                        className={errors.region ? 'error' : ''}
                        disabled={regionsLoading}
                      >
                        <option value="">{regionsLoading ? 'Loading...' : 'Select'}</option>
                        {regions.map((r) => (
                          <option key={r.id} value={r.name}>
                            {r.name}
                          </option>
                        ))}
                      </Select>
                      {regionsError && <ErrorMessage>{regionsError}</ErrorMessage>}
                      {errors.region && <ErrorMessage>{errors.region}</ErrorMessage>}
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="contact" className="font14 semiBold">
                        <FaPhone />
                        Contact *
                      </Label>
                      <Input
                        type="tel"
                        id="contact"
                        name="contact"
                        value={formData.contact}
                        onChange={handleInputChange}
                        className={errors.contact ? 'error' : ''}
                        placeholder="Input Your Phone"
                      />
                      {errors.contact && <ErrorMessage>{errors.contact}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="referralCode" className="font14 semiBold">
                        <FaCode />
                        Referral Code
                      </Label>
                      <Input
                        type="text"
                        id="referralCode"
                        name="referralCode"
                        value={formData.referralCode}
                        onChange={handleInputChange}
                        placeholder="Input Your Referral code"
                      />
                    </FormGroup>
                  </FormRow>

                  <SubmitButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Spinner />
                        Submitting Application...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane />
                        Submit Application
                      </>
                    )}
                  </SubmitButton>
                </Form>
              )}
            </FormCard>
            </AnimateOnScroll>
          </ApplicationForm>

          <InfoSection>
            <InfoCard>
              <h3 className="font20 extraBold">Application Process</h3>
              <InfoList>
                <InfoItem>
                  <span className="step">1</span>
                  <div>
                    <h4 className="font16 semiBold">Submit Application</h4>
                    <p className="font14">Fill out and submit your application form</p>
                  </div>
                </InfoItem>
                <InfoItem>
                  <span className="step">2</span>
                  <div>
                    <h4 className="font16 semiBold">Review & Contact</h4>
                    <p className="font14">We'll review your application and contact you within 48 hours</p>
                  </div>
                </InfoItem>
                <InfoItem>
                  <span className="step">3</span>
                  <div>
                    <h4 className="font16 semiBold">Interview & Assessment</h4>
                    <p className="font14">Complete a brief interview and academic assessment</p>
                  </div>
                </InfoItem>
                <InfoItem>
                  <span className="step">4</span>
                  <div>
                    <h4 className="font16 semiBold">Enrollment</h4>
                    <p className="font14">Begin your journey with AFRIQ Bamenda</p>
                  </div>
                </InfoItem>
              </InfoList>
            </InfoCard>

            <InfoCard>
              <h3 className="font20 extraBold">Why Choose AFRIQ?</h3>
              <BenefitsList>
                <BenefitItem>
                  <h4 className="font16 semiBold">Expert Instructors</h4>
                  <p className="font14">Learn from experienced professionals in your field</p>
                </BenefitItem>
                <BenefitItem>
                  <h4 className="font16 semiBold">Comprehensive Preparation</h4>
                  <p className="font14">Thorough preparation for entrance examinations</p>
                </BenefitItem>
                <BenefitItem>
                  <h4 className="font16 semiBold">Scholarship Opportunities</h4>
                  <p className="font14">Access to various scholarship programs</p>
                </BenefitItem>
                <BenefitItem>
                  <h4 className="font16 semiBold">Career Guidance</h4>
                  <p className="font14">Professional mentorship and career counseling</p>
                </BenefitItem>
              </BenefitsList>
            </InfoCard>
          </InfoSection>
        </div>
      </Wrapper>
      <Footer />
    </>
  );
}

const Wrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
  position: relative;
  padding-top: 80px;
  
  @media (max-width: 768px) {
    padding-top: 60px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 80%, rgba(75, 46, 131, 0.02) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(75, 46, 131, 0.02) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(75, 46, 131, 0.02) 0%, transparent 50%);
    z-index: 1;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  padding: 40px 0 40px;
  position: relative;
  z-index: 2;
  
  h1 {
    margin-bottom: 15px;
    color: #4b2e83;
  }
  
  p {
    color: #333;
    line-height: 1.6;
    max-width: 700px;
    margin: 0 auto;
  }
`;

const ApplicationForm = styled.div`
  max-width: 800px;
  margin: 0 auto 60px;
  position: relative;
  z-index: 2;
`;

const FormCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(75, 46, 131, 0.1);
  transition: all 0.3s ease;
  min-width: 0;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
  }
`;

const FormHeader = styled.div`
  margin-bottom: 30px;
  text-align: center;
  
  h2 {
    color: #4b2e83;
    margin-bottom: 8px;
  }
  
  p {
    color: #333;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  min-width: 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const Label = styled.label`
  color: #4b2e83;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    color: #4b2e83;
    font-size: 14px;
  }
`;

const Input = styled.input`
  padding: 15px 20px;
  border: 2px solid rgba(75, 46, 131, 0.2);
  border-radius: 12px;
  font-size: 16px;
  font-family: inherit;
  transition: all 0.3s ease;
  background: #ffffff !important;
  color: #333 !important;
  
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-text-fill-color: #333 !important;
    -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
    box-shadow: 0 0 0px 1000px #ffffff inset !important;
    background-color: #ffffff !important;
    color: #333 !important;
  }
  
  &:focus {
    outline: none;
    border-color: #4b2e83;
    box-shadow: 0 0 0 3px rgba(75, 46, 131, 0.1);
    background: #ffffff !important;
    color: #333 !important;
  }
  
  &::placeholder {
    color: #999;
  }
  
  &.error {
    border-color: #ff6b6b;
    background: #ffffff !important;
    color: #333 !important;
  }
`;

const Select = styled.select`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 15px 20px;
  border: 2px solid rgba(75, 46, 131, 0.2);
  border-radius: 12px;
  font-size: 16px;
  font-family: inherit;
  transition: all 0.3s ease;
  background: #ffffff !important;
  cursor: pointer;
  color: #333 !important;
  
  option {
    background: #ffffff;
    color: #333;
  }
  
  &:focus {
    outline: none;
    border-color: #4b2e83;
    box-shadow: 0 0 0 3px rgba(75, 46, 131, 0.1);
    background: #ffffff !important;
    color: #333 !important;
  }
  
  &.error {
    border-color: #ff6b6b;
    background: #ffffff !important;
    color: #333 !important;
  }
`;

const ErrorMessageBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 20px;
  background: #fff5f5;
  border: 2px solid #ff6b6b;
  border-radius: 12px;
  margin-bottom: 20px;
  color: #c53030;
  
  svg {
    font-size: 20px;
    flex-shrink: 0;
  }
  
  span {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
  }
`;

const ErrorMessage = styled.span`
  color: #4b2e83;
  font-size: 12px;
  font-weight: 500;
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px 30px;
  background: #4b2e83;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: #3a2466;
    box-shadow: 0 8px 25px rgba(75, 46, 131, 0.3);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  svg {
    font-size: 16px;
    color: #ffffff;
  }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  
  svg {
    font-size: 60px;
    color: #10b981;
    margin-bottom: 20px;
  }
  
  h3 {
    color: #4b2e83;
    margin-bottom: 10px;
  }
  
  p {
    color: #333;
  }
`;

const InfoSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const InfoCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(75, 46, 131, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
  }
  
  h3 {
    color: #4b2e83;
    margin-bottom: 20px;
    text-align: center;
  }
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  
  .step {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #4b2e83;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
    flex-shrink: 0;
  }
  
  h4 {
    color: #4b2e83;
    margin-bottom: 5px;
  }
  
  p {
    color: #333;
    margin: 0;
    line-height: 1.4;
  }
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const BenefitItem = styled.div`
  h4 {
    color: #4b2e83;
    margin-bottom: 8px;
  }
  
  p {
    color: #333;
    margin: 0;
    line-height: 1.4;
  }
`;
